/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx, keyframes } from '@emotion/react';
import type { Interpolation } from '@emotion/react';

import * as React from 'react';
import { useEffect, useState } from 'react';

import useRouter from 'js/lib/useRouter';

import { Button, Divider, Grid, Link, Typography2, breakpoints } from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';
import { ChevronNextIcon } from '@coursera/cds-icons';

import { onClickNavUrl } from 'bundles/cds-labs/components/Modules/utils/utm';
import { getMetaHeight, isMetaNavEnabled } from 'bundles/ent-website/components/MetaNavigation';
import PriorityCTA from 'bundles/ent-website/components/PriorityCTA';
import Promotion from 'bundles/ent-website/components/Promotion';
import EntWebsiteNavTag from 'bundles/ent-website/components/nav/EntWebsiteNavTag';
import { priorityCtaOverride } from 'bundles/ent-website/constants/navigation';
import type { DOMAINS } from 'bundles/ent-website/constants/navigation';
import type {
  LinkIndividualLink,
  LinkObject,
  LinkObjectChildren,
} from 'bundles/ent-website/contentful/core/Navigation';
import { eventingTransform } from 'bundles/ent-website/utils/eventingTransform';
import { isEmptyLink } from 'bundles/ent-website/utils/navUtils';
import { TrackedA } from 'bundles/page/components/TrackedLink2';

import _t from 'i18n!nls/ent-website';

const SOURCE_SANS = `'Source Sans Pro', Arial, sans-serif`;
const MENU_PADDING = '22px';

const opacityFadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

type MobileNavProps = {
  domain: keyof typeof DOMAINS;
  mobileExpanded: boolean;
  navLinks: LinkObject[];
  primaryCta: LinkIndividualLink | undefined | null;
  hideCta?: boolean | null;
  hideNavigationLinks?: boolean | null;
};

const accordionLinkStyle = css`
  font-family: ${SOURCE_SANS};
  font-size: 24px;
  font-weight: 400;
  line-height: 36px;
  letter-spacing: -0.02em;
  text-align: left;
  color: #636363 !important;
`;

export const MobileNav = ({
  mobileExpanded,
  domain,
  navLinks,
  primaryCta,
  hideCta = false,
  hideNavigationLinks = false,
}: MobileNavProps) => {
  const styles: Record<string, Interpolation<Theme>> = {
    mobileNavigation: css`
      display: block;
      position: fixed;
      background: white;
      left: 0;
      right: 0;
      height: calc(100vh - 100px);
      z-index: 10;
      border-top: 1px solid rgb(0 0 0 / 10%);
      overflow-y: scroll;
      top: 55px;
    `,
    mobileNavigationOffset: () => css`
      top: 100px;

      ${breakpoints.down('lg')} {
        top: calc(96px - ${getMetaHeight({ domain })});
      }
    `,
    mobileNavigationContent: () => css`
      animation: ${opacityFadeIn} 0.333s linear;
      margin: var(--cds-spacing-300) 0;
    `,
    mainLink: () => css`
      display: flex;
      justify-content: space-between;
      align-content: center;
      flex-direction: row;
      align-items: center;
      margin-top: var(--cds-spacing-300);
      cursor: pointer;
      padding-left: ${MENU_PADDING};
      padding-right: ${MENU_PADDING};

      > * {
        font-family: ${SOURCE_SANS};
        font-size: 24px;
        font-weight: 400;
        line-height: 36px;
        letter-spacing: -0.02em;
        text-align: left;
        color: #636363 !important;
      }
    `,
    mobileButton: css`
      margin-top: 12px;
      margin-left: ${MENU_PADDING};
      font-size: 16px;
      line-height: 1.5;
      font-family: ${SOURCE_SANS};
      padding: 12px 32px;
    `,
    accordion: css`
      .cds-AccordionRoot-focusContainer {
        margin-left: -2px;
        margin-right: -2px;
      }

      .cds-AccordionRoot-container {
        border: none;
      }
    `,
    accordionHeader: () => css`
      padding-left: 0 !important;
      padding-right: 0 !important;

      button {
        padding-left: ${MENU_PADDING} !important;
        padding-right: ${MENU_PADDING} !important;
        padding-top: 14px !important;
        padding-bottom: 14px !important;
        line-height: 1;

        &:hover {
          background: white !important;
        }

        span {
          ${accordionLinkStyle}
          color: var(--cds-color-grey-975) !important;
        }
      }

      svg {
        position: absolute;
        right: ${MENU_PADDING};
        margin-top: 4px !important;
        top: 20px;
        width: 14px;
        height: 14px;
        margin-right: 0 !important;
      }
    `,
    accordionPanel: css`
      padding-left: 0;
      padding-right: 0;
      margin-left: 0;
      margin-right: 0;
      margin-bottom: 0;
      padding-bottom: 12px;

      a:nth-child(1) {
        margin-top: 0;
      }
    `,
    breadCrumbContainer: () => css`
      display: flex;
      justify-content: start;
      align-content: center;
      align-items: center;
      padding-bottom: 1px solid rgb(0 0 0 / 10%);
      padding-left: ${MENU_PADDING};
      padding-right: ${MENU_PADDING};

      > * {
        margin: var(--cds-spacing-100) var(--cds-spacing-100) var(--cds-spacing-100) 0;
        padding: var(--cds-spacing-100) 0;
        margin-bottom: var(--cds-spacing-100);
        box-sizing: content-box;
      }
    `,
    mainMenuLink: css`
      font-family: ${SOURCE_SANS};
      font-size: 14px;
      font-weight: 400;
      line-height: 20px;
      letter-spacing: -0.02em;
      text-align: left;
      padding-left: 0;
      padding-right: 0;
    `,
    mainMenuLinkCrumb: css`
      font-family: ${SOURCE_SANS};
      font-size: 14px;
      font-weight: 600;
      line-height: 21px;
      letter-spacing: -0.03em;
      text-align: left;
    `,

    linkHeading: css`
      font-family: ${SOURCE_SANS};
      font-size: 20px;
      font-weight: 600;
      line-height: 20px;
      letter-spacing: -0.03em;
      text-align: left;
      margin-bottom: 8px;
    `,
    linkDescription: css`
      font-family: ${SOURCE_SANS};
      font-size: 14px;
      font-weight: 400;
      line-height: 20px;
      letter-spacing: -0.02em;
      text-align: left;
    `,
    linkSubHeading: css`
      font-family: ${SOURCE_SANS};
      font-size: 14px;
      font-weight: 600;
      line-height: 21px;
      letter-spacing: -0.03em;
      text-align: left;
    `,
    linkSubHeadingCnt: css`
      margin-bottom: 4px;
    `,
    linkSubDescription: css`
      font-family: ${SOURCE_SANS};
      font-size: 14px;
      font-weight: 400;
      line-height: 20px;
      letter-spacing: -0.02em;
      text-align: left;
    `,
    divider: () => css`
      margin-top: var(--cds-spacing-150);
      margin-bottom: var(--cds-spacing-300);
    `,
    gutteredDivider: css`
      margin-left: 24px;
      margin-right: 24px;
      margin-top: 0;
    `,
    linkContainer: () => css`
      display: block;
      margin-top: var(--cds-spacing-800);

      &:first-of-type {
        margin-top: 40px;
      }
    `,
    subLinkContainer: () => css`
      display: block;
      margin-bottom: var(--cds-spacing-300);
    `,
    accordionLink: () => css`
      display: flex;
      justify-content: space-between;
      align-content: center;
      flex-direction: row;
      align-items: center;
      margin-bottom: var(--cds-spacing-300);
      cursor: pointer;
      padding: 8px 0;
      margin: 0;
      padding-left: ${MENU_PADDING};
      padding-right: ${MENU_PADDING};

      p {
        ${accordionLinkStyle}
        color: var(--cds-color-grey-975) !important;
      }

      &:nth-child(1) {
        margin-top: var(--cds-spacing-600);
      }
    `,
    accordionSubLink: css`
      padding-left: calc(${MENU_PADDING} + 24px);
    `,
    activeAccordionLink: () => css`
      background-color: var(--cds-color-grey-200);
    `,
    iconCheck: () => css`
      color: var(--cds-color-grey-975);
    `,
    paddedContainer: css`
      padding-left: ${MENU_PADDING};
      padding-right: ${MENU_PADDING};
    `,
    ctaContainer: () => css`
      margin-top: var(--cds-spacing-300);
    `,
  };

  const router = useRouter();
  const [mobileMenuItem, setMobileMenuItem] = useState('');

  useEffect(() => {
    if (!mobileExpanded) {
      setMobileMenuItem('');
    }
  }, [mobileExpanded]);

  const trackingNameBreadCrumb0 = eventingTransform('navigation_mobile_menu_breadcrumb0');
  const metaNavEnabled = isMetaNavEnabled({ domain });

  const toggleMobileMenuItem = (menuItem: string) => () => {
    setMobileMenuItem(menuItem);
  };

  if (!mobileExpanded) {
    return null;
  }

  function renderItems(childObj: LinkObjectChildren) {
    return childObj.items?.map((item: LinkIndividualLink | null) => {
      const trackingName = eventingTransform(`navigation_mobile_menu_link_${item?.ctaText}`);
      return (
        <Link
          key={trackingName}
          component={TrackedA}
          trackingName={trackingName}
          variant="quiet"
          href={item?.ctaTarget ?? ''}
          target={item?.shouldOpenInNewWindow ? '_blank' : undefined}
          onClick={item?.shouldOpenInNewWindow ? undefined : onClickNavUrl(router, item?.ctaTarget ?? '')}
          css={styles.subLinkContainer}
        >
          <Grid container direction="row" alignItems="center" css={styles.linkSubHeadingCnt}>
            <Typography2 component="p" css={styles.linkSubHeading}>
              {item?.ctaText}
            </Typography2>
            {item?.showNewBadge && <EntWebsiteNavTag label={_t('New')} />}
          </Grid>
          <Typography2 component="p" css={styles.linkSubDescription}>
            {item?.ctaDescription}
          </Typography2>
        </Link>
      );
    });
  }

  function renderPromotion(childObj: LinkObjectChildren) {
    return <Promotion promotion={childObj.promotion} />;
  }

  if (mobileMenuItem) {
    return (
      <div css={styles.mobileNavigation}>
        <div css={styles.mobileNavigationContent}>
          <div css={styles.breadCrumbContainer}>
            <Button
              variant="ghost"
              onClick={toggleMobileMenuItem('')}
              css={styles.mainMenuLink}
              component={TrackedA}
              trackingName={trackingNameBreadCrumb0}
            >
              Main Menu
            </Button>
            <ChevronNextIcon size="medium" />
            <span css={styles.mainMenuLinkCrumb}>{mobileMenuItem}</span>
          </div>
          <Divider />
          <div css={styles.paddedContainer}>
            {navLinks
              .filter((link) => link.text === mobileMenuItem)
              .map((link) => {
                return link?.children?.map((childObj) => {
                  return (
                    <div key={childObj?.heading}>
                      {childObj.heading && (
                        <div css={styles.linkContainer}>
                          <Typography2 component="p" css={styles.linkHeading}>
                            {childObj.heading}
                          </Typography2>
                          <Typography2 component="p" css={styles.linkDescription}>
                            {childObj.description}
                          </Typography2>

                          <Divider css={styles.divider} />
                        </div>
                      )}
                      {renderPromotion(childObj)}
                      {renderItems(childObj)}
                    </div>
                  );
                });
              })}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div css={[styles.mobileNavigation, metaNavEnabled ? styles.mobileNavigationOffset : null]}>
      <div css={styles.mobileNavigationContent}>
        {!hideNavigationLinks &&
          navLinks.map((link) => {
            const trackingName = eventingTransform(`navigation_mobile_menu_step0_${link.text}`);
            const hasChildren = Number(link?.children?.length ?? 0) > 0;

            return (
              // eslint-disable-next-line jsx-a11y/anchor-is-valid
              <Link
                component={TrackedA}
                trackingName={trackingName}
                css={styles.mainLink}
                key={link.text}
                href={isEmptyLink(link) ? '#' : link.href}
                variant="quiet"
                onClick={
                  link.children?.length ? toggleMobileMenuItem(link.text) : onClickNavUrl(router, link.href ?? '#')
                }
              >
                <Typography2 component="p">{link.text}</Typography2>
                {hasChildren && <ChevronNextIcon size="medium" />}
              </Link>
            );
          })}

        {!hideCta && priorityCtaOverride[domain] && (
          <div css={styles.ctaContainer}>
            <PriorityCTA primaryCta={primaryCta} {...priorityCtaOverride[domain]} />
          </div>
        )}
      </div>
    </div>
  );
};

export default MobileNav;
